/**
 * PanelrContext.tsx - PanelrContext - React context wrapper for Panelr
 * 
 * CHANGE HISTORY:
 * - 251021_0729 [dfa05af] danielbeer: fix(panelr): fix context and all tests now passing
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
// @ts-nocheck - TODO: Fix TypeScript errors systematically
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { PanelrManager } from './PanelrManager';
import type { PanelrContextValue, PanelConfig, PanelMode, PanelPosition, UsePanelReturn } from './types';

const PanelrContext = createContext<PanelrContextValue | null>(null);

interface PanelrProviderProps {
  children: ReactNode;
  namespace?: string;
  options?: any;
}

export const PanelrProvider: React.FC<PanelrProviderProps> = ({ children, namespace = 'app', options = {} }) => {
  // console.log('[PanelrProvider] Rendering with namespace:', namespace); // Disabled - too noisy
  const panelrRef = useRef(null);
  const [, forceUpdate] = useState({});
  
  // Initialize Panelr
  if (!panelrRef.current) {
    // console.log('[PanelrProvider] Creating new PanelrManager instance'); // Disabled - too noisy
    panelrRef.current = new PanelrManager({});
  }
  
  const panelr = panelrRef.current;
  // console.log('[PanelrProvider] Using panelr instance:', panelr); // Disabled - too noisy
  
  // Update CSS variables based on panel states
  const updateContentAreaVars = useCallback(() => {
    const area = panelr.calculateContentArea();
    const root = document.documentElement;
    
    // console.log('[Panelr] Setting CSS variables:', area); // Disabled - too noisy
    root.style.setProperty('--panelr-content-top', `${area.top}px`);
    root.style.setProperty('--panelr-content-left', `${area.left}px`);
    root.style.setProperty('--panelr-content-right', `${area.right}px`);
    root.style.setProperty('--panelr-content-bottom', `${area.bottom}px`);
  }, [panelr]);
  
  // Subscribe to state changes and force re-render
  useEffect(() => {
    // console.log('[PanelrContext] Setting up state-changed subscription'); // Disabled - too noisy
    const unsubscribe = panelr.on('state-changed', (event) => {
      // console.log('[PanelrContext] Received state-changed event:', event); // Disabled - too noisy
      forceUpdate({});
      // Update CSS variables for content area
      updateContentAreaVars();
    });
    
    return unsubscribe;
  }, [panelr, updateContentAreaVars]);
  
  // Initial update
  useEffect(() => {
    updateContentAreaVars();
    
    // Expose Panelr globally for debugging
    if (typeof window !== 'undefined') {
      (window as any).panelr = panelr;
      // console.log('[Panelr] Instance exposed as window.panelr'); // Disabled - too noisy
    }
  }, [updateContentAreaVars, panelr]);
  
  // Create stable callback references
  const register = useCallback((id, config) => panelr.register(id, config), [panelr]);
  const unregister = useCallback((id) => panelr.unregister(id), [panelr]);
  const open = useCallback((id) => panelr.open(id), [panelr]);
  const close = useCallback((id) => panelr.close(id), [panelr]);
  const toggle = useCallback((id) => panelr.toggle(id), [panelr]);
  const resize = useCallback((id, width) => panelr.resize(id, width), [panelr]);
  const setPosition = useCallback((id, position) => panelr.setPosition(id, position), [panelr]);
  const setMode = useCallback((id, mode) => panelr.setMode(id, mode), [panelr]);
  const getMode = useCallback((id) => panelr.getMode(id), [panelr]);
  const cycleMode = useCallback((id) => panelr.cycleMode(id), [panelr]);
  const getState = useCallback((id) => panelr.getState(id), [panelr]);
  const getAllPanels = useCallback(() => panelr.getAllPanels(), [panelr]);
  const getPanelsByPosition = useCallback((position) => panelr.getPanelsByPosition(position), [panelr]);
  const saveState = useCallback((id) => panelr.saveState(id), [panelr]);
  const clearState = useCallback((id) => panelr.clearState(id), [panelr]);
  const setShowBorders = useCallback((show) => panelr.setShowBorders(show), [panelr]);
  const setColorCodeLevels = useCallback((enable) => panelr.setColorCodeLevels(enable), [panelr]);
  const getVisualSettings = useCallback(() => panelr.getVisualSettings(), [panelr]);
  const calculateContentArea = useCallback(() => panelr.calculateContentArea(), [panelr]);
  const reset = useCallback((id) => panelr.reset(id), [panelr]);

  const value = useMemo(() => ({
    panelr,
    register,
    unregister,
    open,
    close,
    toggle,
    resize,
    setPosition,
    setMode,
    getMode,
    cycleMode,
    getState,
    getAllPanels,
    getPanelsByPosition,
    reset,
    clearState,
    setShowBorders,
    setColorCodeLevels,
    getVisualSettings,
    calculateContentArea
  }), [panelr, register, unregister, open, close, toggle, resize, setPosition, setMode, getMode, cycleMode, getState, getAllPanels, getPanelsByPosition, reset, clearState, setShowBorders, setColorCodeLevels, getVisualSettings, calculateContentArea]);
  
  return (
    <PanelrContext.Provider value={value}>
      {children}
    </PanelrContext.Provider>
  );
};

/**
 * Hook to access Panelr context
 */
export const usePanelr = () => {
  const context = useContext(PanelrContext);
  
  if (!context) {
    throw new Error('usePanelr must be used within PanelrProvider');
  }
  
  return context;
};

/**
 * Hook to manage a specific panel
 */
export const usePanel = (id, config = {}) => {
  const { panelr, register, unregister, open, close, toggle, getState } = usePanelr();
  const [state, setState] = useState(() => getState(id));
  
  // Register panel on mount and update state
  useEffect(() => {
    register(id, config);
    // Update state immediately after registration
    setState(getState(id));
    
    return () => {
      unregister(id);
    };
  }, [id]);
  
  // Subscribe to state changes
  useEffect(() => {
    const unsubscribe = panelr.on('state-changed', ({ id: changedId, state: newState }) => {
      if (changedId === id) {
        console.log(`[usePanel ${id}] State changed event:`, newState);
        const freshState = getState(id);
        console.log(`[usePanel ${id}] Getting fresh state:`, freshState);
        console.log(`[usePanel ${id}] Width in fresh state:`, freshState?.width);
        setState(freshState);
        console.log(`[usePanel ${id}] setState called with width:`, freshState?.width);
      }
    });
    
    return unsubscribe;
  }, [id, panelr, getState]);
  
  return {
    state,
    isOpen: state?.isOpen ?? false,
    width: state?.width,
    position: state?.position,
    open: () => open(id),
    close: () => close(id),
    toggle: () => toggle(id)
  };
};

/**
 * Hook for panel resize functionality
 */
export const usePanelResize = (id) => {
  const { resize, getState } = usePanelr();
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  const handleMouseDown = useCallback((e) => {
    const state = getState(id);
    if (!state || !state.resizable) return;
    
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = state.width;
    
    e.preventDefault();
  }, [id, getState]);
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      const delta = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + delta;
      resize(id, newWidth);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, id, resize]);
  
  return {
    isDragging,
    handleMouseDown
  };
};

export default PanelrContext;
